<script>
  import ClosableModal from 'root/services/modals/Closable.modal.svelte';
  import SpinnerWithText from 'root/components/molecules/SpinnerWithText.svelte';
  import ModalButtons from 'root/services/modals/ModalButtons.YesNo.svelte';
  import manageUsersService
    , {PENDING_USER_TYPE} from 'vRoot/account-profile/modules/manage-users/services/account-manage-users-list.service';
  import orderBy from 'lodash/orderBy';
  import {notificationUtils, mainAPI} from 'root/angular-injector-provider';
  import getSelectionIcon from 'root/services/selection-icons.service';
  import {comparisonForStrings} from 'root/services/tools';

  export let hide, cancel, accountId, company;

  const
    preselected = [],
    LOADING = 'Loading';

  let
    users = LOADING,
    working = true,
    selected = [],
    selectionModified = false,
    notify = true;

  (async function initialize() {
    const preselectedManagers = (company.managers || []).map(m => m.userId);
    let result = await manageUsersService.loadUsers();

    result = result.map(user => {
      user.managedAccountsCount = user.accountsManaged.length;
      if(preselectedManagers.includes(user.id)) {
        user.existingManager = true;
        preselected.push(user);
      }
      return user;
    });

    users = orderBy(result, ['existingManager', 'fullName'], ['asc', 'asc']);
    selected = [...preselected];
    working = false;
  })();

  function save(){
    if(selectionModified){
      working = true;
      const managers = selected.map(s => ({
        userId: s.id,
        isUser: s.type !== PENDING_USER_TYPE
      }));

      notificationUtils().onSave(() => mainAPI().setAccountManagers(accountId, company.id, managers, notify))
        .then(hide);
    }
  }

  function toggleUserSelection(user){
    selected = selected.includes(user)? selected.filter(s => s !== user): [...selected, user];
    selectionModified = selected.length !== preselected.length || selected.filter(s => !s.existingManager).length;
  }
</script>

<ClosableModal width="500px" close="{cancel}">
  <div slot="heading" class="Heading">
    <div class="Logo">
      {#if company.logo}
        <img src="{`/images/logos/${company.logo}`}" alt="{company.name}" />
      {:else}
        <div data-icon-before="business"></div>
      {/if}
    </div>
    <div class="Details">
      <div class="Title">Assign an account manager</div>
      <div>to { company.name }</div>
    </div>
  </div>
  {#if users === LOADING}
    <div class="Notification">
      <SpinnerWithText />
    </div>
  {:else if !users.length}
    <div class="Notification">
      There are no users to assign!
    </div>
  {:else}
    <div class="Body">
      {#each users as user}
        <div class="User"
             class:ExistingManager={user.existingManager}
             class:Selected={selected.includes(user)}
             data-icon-before="{getSelectionIcon(selected.includes(user))}"
             on:click={() => toggleUserSelection(user)}
        >
          <div class="UserName">{user.fullName}</div>

          {#if user.managedAccountsCount}
            <div>
              Managing {user.managedAccountsCount} Compan{user.managedAccountsCount === 1? 'y': 'ies'}
              <div class="rbTooltip simple ManagersTooltip" data-tooltip="placement:top-end">
                <div>Accounts Managed:</div>
                <div>{user.accountsManaged.map(ma => ma.accountDetails.name).sort(comparisonForStrings).join(', ')}</div>
              </div>
            </div>
          {:else}
            <div class="Unassigned" data-icon-after="warning">
              Not Assigned
            </div>
          {/if}
        </div>
      {/each}
    </div>

    <div class="NotifyCheckbox"
         class:On="{notify}"
         data-icon-before="{getSelectionIcon(notify)}"
         on:click={() => notify = !notify}
    >
      Notify Managers of changes.
    </div>
  {/if}

  <ModalButtons
    disabled="{working || !selectionModified}"
    noAction="{cancel}"
    noLabel="Cancel"
    yesLabel="Apply Changes"
    yesIcon="account_circle"
    yesAction="{save}"
  />
</ClosableModal>


<style lang="stylus">

  .Heading {
    position: relative;
    padding: 14px 10px 0;
    font-size: 13px;
    color: #78909C;
    font-weight: 600;
    align-items: center;
    display: flex;
  }

  .Logo {
    height: 65px;
    width: 110px;
    object-fit: contain;
    border-radius: 3px;
    border: 1px solid #eceff1;
    background: #eceff1;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    margin-right: 10px;
    overflow: hidden;

    div:before {
      color: #37474f;
      font-size: 50px;
    }

    img {
      max-height: 65px;
      max-width: 110px;
    }
  }

  .Title {
    color: #37474f;
    font-size: 16px;
  }

  .Notification {
    padding: 20px;
    font-size: 14px;
    font-weight: bold;
    color #546E7A;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .Body {
    max-height: 500px;
    margin: 15px;
    color #546E7A;
    font-size 13px;
    overflow: auto;
    border: 1px solid #b0bec5;
    border-radius: 3px;
  }

  .User {
    display: flex;
    align-items: center;
    height: 42px;
    box-sizing: border-box;
    padding: 10px;

    border-bottom: 1px solid #eceff1;
    cursor: pointer;
    font-size: 13px;

    & > div {
      white-space: nowrap;
    }

    &:hover {
      background rgba(252, 238, 33, 0.08);
    }

    &.Selected {
      background rgba(252, 238, 33, 0.24);
    }


    &:before {
      font-size: 16px;
      margin-right: 5px;
    }
  }

  :nth-last-child(1 of .ExistingManager) {
    border-bottom 3px solid #CFD8DC;
  }

  .UserName {
    flex: 1 1 100%;
  }

  .Unassigned {
    color: #F15A24;

    &:after {
      margin-left: 3px;
      font-size: 18px;
    }
  }

  .NotifyCheckbox {
    margin: 0 15px;
    color: #546e7a;
    font-size: 12px;
    gap: 5px;
    cursor: pointer;
    width: fit-content;


    &:before {
      font-size: 18px;
      height: 18px;
    }

    &.On {
      color: #00A99D;
    }

    &:hover {
      color: #37474f;
    }
  }

  .ManagersTooltip {
    max-width: 400px;
    text-align: left;

    div:first-child {
      text-align: center;
      font-weight: bold;
    }
  }
</style>
